import { useState } from 'react'
import { userAvatarUrl } from '@/lib/avatar/startingAvatar'
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import { authHrefWithReturn, currentReturnPath, readRequestedReturnPath } from '@/lib/auth/returnTo'
import { Menu, X, Search, Coins, Volume2, VolumeX, LogOut, LogIn } from 'lucide-react'
import { getActiveWorldConfig } from '@gamecodex/world-config'
import { cn } from '@/lib/cn'
import { useUserStore } from '@/stores/userStore'
import { useCoinStore } from '@/stores/coinStore'
import { useAudioStore } from '@/stores/audioStore'
import { useCoinBalanceSync } from '@/lib/api/coins'
import { useAuth } from '@/lib/auth/useAuth'
import { useT, useLocalizedHref } from '@/i18n'
import { GlobalSearch } from '@/features/encyclopedia/GlobalSearch'
import { useCanEditCityMaps } from '@/features/city/editor/mapEditorAccess'
import { KitHeader } from './KitHeader'
import { ViewToggle } from '@/features/city/three/FirstUi'

const NAV = [
  { to: '/', key: 'layout.nav.veil', end: true },
  { to: '/encyclopedia', key: 'layout.nav.encyclopedia' },
  { to: '/world', key: 'layout.nav.world' },
  // a world with a game (a player home) opens its play page, where the player picks a mode (see navTo); others keep the tables
  { to: '/play', key: 'layout.nav.play' },
  { to: '/books', key: 'layout.nav.books' },
  { to: '/community', key: 'layout.nav.community' },
]

/** Where a nav item goes in this world: Play opens the mode picker in a world with a game. Read at render time, never
 *  when the module loads (the world config is not set yet then). */
const navTo = (to: string): string => (to === '/play' && getActiveWorldConfig().playerHome ? '/play/start' : to)

/** The city game screen (not the staff map editor). */
export const isGameRoute = (path: string): boolean => /\/world\/cities\/(?!editor\/?$)[^/]+\/?$/.test(path)

export function TopNav() {
  // a world with a painted header kit (EvolveRPG) shows it over the city game (the header belongs to the game, Jared
  // 11/2); the rest of the site, and every other world, keep this one
  const kit = getActiveWorldConfig().branding.headerKit
  const game = isGameRoute(useLocation().pathname)
  return kit && game ? <KitHeader kit={kit} /> : <PlainNav />
}

function PlainNav() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const user = useUserStore((s) => s.user)
  const balance = useCoinStore((s) => s.balance)
  const balanceKnown = useCoinStore((s) => s.hydrated)
  // The header owns keeping the balance current, so it is right on every page, not only after visiting /me.
  useCoinBalanceSync(user?.id)
  const muted = useAudioStore((s) => s.muted)
  const toggleMuted = useAudioStore((s) => s.toggleMuted)
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const t = useT()
  // The city map editor is a staff tool (the god account and shardmasters): its link shows only to them.
  const canEditMaps = useCanEditCityMaps() === true
  const items = canEditMaps ? [...NAV, { to: '/world/cities/editor', key: 'city.editor.title' }] : NAV
  const localized = useLocalizedHref()
  const location = useLocation()
  // "Enter" carries the current page so sign-in returns here, not to /me.
  const loginHref = localized(authHrefWithReturn('/login', readRequestedReturnPath(location.state, location.search) ?? currentReturnPath(location)))
  const { branding, worldName } = getActiveWorldConfig()

  return (
    <header data-site-header className="sticky top-0 z-40 border-b border-blood-900/60 bg-obsidian-500/85 backdrop-blur-md">
      <div className="mist-layer absolute inset-0 opacity-30" aria-hidden />
      <nav className="relative mx-auto flex h-[var(--site-header-h,4rem)] w-full items-center gap-4 px-4">
        <Link to={localized('/')} className="shrink-0 transition-opacity hover:opacity-90" aria-label={t('layout.nav.homeAria', { world: worldName })}>
          <img src={branding.images.logo} alt={worldName} className="h-9 w-auto sm:h-10" />
        </Link>

        <ul className="ml-6 hidden items-center gap-1 lg:flex">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={localized(navTo(item.to))}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'rounded-sm px-3 py-2 font-ui text-xs uppercase tracking-wide transition-colors',
                    isActive ? 'text-gold' : 'text-bone-muted hover:text-bone',
                  )
                }
              >
                {t(item.key)}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setSearchOpen(true)} aria-label={t('layout.nav.search')} className="rounded-sm p-2 text-bone-muted hover:text-gold">
            <Search size={18} />
          </button>
          <button onClick={toggleMuted} aria-label={muted ? t('layout.nav.unmute') : t('layout.nav.mute')} className="rounded-sm p-2 text-bone-muted hover:text-gold">
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>

          {/^\/([a-z]{2}(-[A-Za-z]+)?\/)?world\/cities\/(?!editor)[^/]+\/?$/.test(location.pathname) && <ViewToggle />}
          {user ? (
            <>
              <Link to={localized('/me')} className="flex items-center gap-1 rounded-sm border border-gold/30 px-2 py-1 text-gold" title={balanceKnown ? t('layout.nav.coinBalance') : t('layout.nav.coinBalanceUnavailable')} aria-label={balanceKnown ? `${t('layout.nav.coinBalance')}: ${balance}` : t('layout.nav.coinBalanceUnavailable')}>
                <Coins size={16} />
                <span className="font-ui text-sm">{balanceKnown ? balance.toLocaleString() : '?'}</span>
              </Link>
              <Link
                to={localized(getActiveWorldConfig().playerHome ?? '/me')}
                className="ml-1 flex items-center gap-2 rounded-sm py-1 pl-1 pr-2 hover:bg-blood-900/20"
                title={t('layout.nav.signedInAs', { name: user.displayName })}
              >
                <img
                  src={userAvatarUrl(user) ?? branding.images.avatarFallback}
                  alt=""
                  className="h-8 w-8 rounded-full border border-gold/40 object-cover"
                />
                <span className="hidden max-w-[8rem] truncate font-ui text-xs text-bone sm:inline">{user.displayName}</span>
              </Link>
              <button
                onClick={() => {
                  void signOut().then(() => navigate(localized('/')))
                }}
                aria-label={t('layout.nav.signOut')}
                title={t('layout.nav.signOut')}
                className="rounded-sm p-2 text-bone-muted hover:text-gold"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <Link to={loginHref} aria-label={t('layout.nav.signIn')} title={t('layout.nav.signIn')} className="rounded-sm p-2 text-bone-muted hover:text-gold">
              <LogIn size={18} />
            </Link>
          )}

          <button onClick={() => setMobileOpen((v) => !v)} aria-label={t('layout.nav.toggleMenu')} className="rounded-sm p-2 text-bone-muted hover:text-bone lg:hidden">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <ul className="relative border-t border-blood-900/60 bg-obsidian-400/95 px-4 py-2 lg:hidden">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={localized(navTo(item.to))}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => cn('block py-2 font-ui text-sm uppercase tracking-wide', isActive ? 'text-gold' : 'text-bone-muted')}
              >
                {t(item.key)}
              </NavLink>
            </li>
          ))}
          {user ? (
            <>
              <li>
                <NavLink to={localized('/me')} onClick={() => setMobileOpen(false)} className="block py-2 font-ui text-sm uppercase tracking-wide text-bone-muted">
                  {t('layout.nav.dashboard', { world: worldName, name: user.displayName })}
                </NavLink>
              </li>
              <li>
                <button
                  onClick={() => {
                    setMobileOpen(false)
                    void signOut().then(() => navigate(localized('/')))
                  }}
                  className="flex w-full items-center gap-2 py-2 font-ui text-sm uppercase tracking-wide text-bone-muted hover:text-gold"
                >
                  <LogOut size={16} /> {t('layout.nav.signOut')}
                </button>
              </li>
            </>
          ) : (
            <li>
              <NavLink to={loginHref} onClick={() => setMobileOpen(false)} className="block py-2 font-ui text-sm uppercase tracking-wide text-gold">
                {t('layout.nav.enter', { world: worldName })}
              </NavLink>
            </li>
          )}
        </ul>
      )}

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  )
}
